import type { TaskWithRelations } from '@vagusx/clients';
import { ScheduleAppointmentDrawer } from './ScheduleAppointmentDrawer';

export interface ActionDrawerEntry {
  /** Unique key — used both for React keys and dev-tool inspection. */
  id: string;
  /**
   * Short imperative phrase shown as the "Next step" headline above the
   * action button. Drives the human-readable framing — the button label
   * itself stays terse.
   */
  headline: string;
  /**
   * Predicate over the task's labels[] (already resolved to slug form).
   * Returning true makes this drawer visible for the task. The first
   * matching entry wins; we render at most one action button per task
   * for now to keep the overview header uncluttered.
   */
  matches: (labels: readonly string[]) => boolean;
  /**
   * Drawer/component renderer. Owns its open/close state — the registry
   * just decides which one to mount.
   */
  render: (task: TaskWithRelations) => React.ReactNode;
}

/**
 * Pluggable registry for task-level action drawers. Today there's a
 * single entry — the schedule-appointment drawer for referrals — but
 * the lookup-by-classification shape is the contract: dropping a new
 * drawer in `action-drawers/` and appending it here is the whole
 * extension surface.
 */
export const ACTION_DRAWERS: ActionDrawerEntry[] = [
  {
    id: 'schedule-appointment',
    headline: 'Reach out to schedule the consult',
    matches: (labels) => labels.some((l) => l === 'referral' || l === 'patient_referral'),
    render: (task) => <ScheduleAppointmentDrawer task={task} />,
  },
];

export function pickActionDrawer(task: TaskWithRelations): ActionDrawerEntry | null {
  for (const entry of ACTION_DRAWERS) {
    if (entry.matches(task.labels ?? [])) return entry;
  }
  return null;
}
